import { Logger } from '@mobalytics/shared';
import pRetry from 'p-retry';
import * as React from 'react';
import { BattlePassRewardsAccess } from '../../../__generated/treasury/types';
import { treasuryApolloClient } from '../../../treasury-apollo-client';
import { usePaymentV2BattlePassProductQueryLazyQuery } from '../_gql/__generated/payment-v2-battle-pass-product.query.gql.generated';
import type { ProductI } from '../payment.types';

type ReturnT =
  | {
      state: 'loading';
    }
  | {
      state: 'error';
    }
  | {
      state: 'success';
    };

export const useThankYou = (product: ProductI): ReturnT => {
  const [loading, setLoading] = React.useState<boolean>(true);
  const [hasError, setHasError] = React.useState<boolean>(false);

  const [refetch, { data, error: errorBattlePass, loading: loadingBattlePass }] =
    usePaymentV2BattlePassProductQueryLazyQuery({
      client: treasuryApolloClient,
      errorPolicy: 'all',
      // we dont want to get cached info, only actual data from the server
      fetchPolicy: 'network-only',
    });

  const validateBattlePassWithRetry = React.useCallback(async () => {
    if (product.__typename !== 'TreasuryProductBattlePass') {
      Logger.error(`[validateBattlePassWithRetry]: product is not a battle pass`, product);
      return;
    }
    setLoading(true);
    return pRetry(
      async () => {
        const refetchResult = await refetch({
          variables: {
            input: {
              id: product.id,
            },
          },
        });
        const rewardsAccess = refetchResult.data?.battlePass.battlePass.data?.enrollment?.rewardsAccess;
        if (rewardsAccess !== BattlePassRewardsAccess.PAID) {
          throw new Error(`[validateBattlePassWithRetry]: incorrect rewardsAccess = ${rewardsAccess}`);
        }
        setLoading(false);
      },
      {
        retries: 4,
        onFailedAttempt: (error) => {
          Logger.warn(
            `[validateBattlePassWithRetry]: failed attempt: ${error.message} retries left: ${error.retriesLeft}`
          );
          if (error.retriesLeft === 0) {
            Logger.error(`[validateBattlePassWithRetry]: failed attempt: ${error.message}`);
            setLoading(false);
            setHasError(true);
          }
        },
      }
    );
  }, [product, refetch]);

  const validateXpBoosterWithRetry = React.useCallback(async () => {
    if (product.__typename !== 'TreasuryProductXPBooster') {
      Logger.error(`[validateXpBoosterWithRetry]: product is not a XP booster`, product);
      return;
    }
    setLoading(true);
    return pRetry(
      async () => {
        const refetchResult = await refetch({
          variables: {
            input: {
              id: product.battlePassId,
            },
          },
        });
        const isBoosterActive = refetchResult.data?.battlePass.battlePass.data?.booster?.active;
        if (!isBoosterActive) {
          throw new Error(`[validateXpBoosterWithRetry]: booster is not active`);
        }
        setLoading(false);
      },
      {
        retries: 4,
        onFailedAttempt: (error) => {
          Logger.warn(
            `[validateXpBoosterWithRetry]: failed attempt: ${error.message} retries left: ${error.retriesLeft}`
          );
          if (error.retriesLeft === 0) {
            Logger.error(`[validateXpBoosterWithRetry]: failed attempt: ${error.message}`);
            setLoading(false);
            setHasError(true);
          }
        },
      }
    );
  }, [product, refetch]);

  const checkStatus = React.useCallback(async () => {
    try {
      if (product.__typename === 'TreasuryProductBattlePass') {
        await validateBattlePassWithRetry();
      }
      if (product.__typename === 'TreasuryProductXPBooster') {
        await validateXpBoosterWithRetry();
      }
    } catch (e) {
      Logger.error('[checkStatus]: unknown error', e);
      setHasError(true);
    }
  }, [product.__typename, validateBattlePassWithRetry, validateXpBoosterWithRetry]);

  React.useEffect(() => {
    checkStatus();
  }, [checkStatus]);

  if (loading || loadingBattlePass) {
    return {
      state: 'loading',
    };
  }

  if (hasError || errorBattlePass || data?.battlePass.battlePass.error) {
    return {
      state: 'error',
    };
  }

  return {
    state: 'success',
  };
};
